/**
 * Page for billing info collection during instance creation
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import Drawer from '@material-ui/core/Drawer';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ORTypography from 'components/ORTypography/ORTypography';
import Close from '@material-ui/icons/Close';
import CreditCard from '@material-ui/icons/CreditCard';
import Fab from '@material-ui/core/Fab';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import AppBar from '@material-ui/core/AppBar';
import Cake from '@material-ui/icons/Cake';
import Hidden from '@material-ui/core/Hidden';

import BillingFormWrapper from 'components/BillingFormWrapper/BillingFormWrapper';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  completeBillingSetup: PropTypes.func.isRequired,
  getOrg: PropTypes.func.isRequired,
  trialCreditCents: PropTypes.number,
  open: PropTypes.bool.isRequired,
  billingOpen: PropTypes.func.isRequired,
  price: PropTypes.number.isRequired,
  createInstance: PropTypes.func.isRequired,
  newInstanceData: PropTypes.shape({}).isRequired,
  redirectToInstances: PropTypes.func.isRequired,
  isBillingUser: PropTypes.bool.isRequired,
};

const defaultProps = {
  trialCreditCents: 20000,
};

export function UnstyledBillingPage({
  classes,
  completeBillingSetup,
  getOrg,
  trialCreditCents,
  open,
  billingOpen,
  price,
  createInstance,
  newInstanceData,
  redirectToInstances,
  isBillingUser,
}) {
  const billingCompleteHandler = () => {
    completeBillingSetup();
    getOrg().then(() => createInstance(newInstanceData, redirectToInstances));
  };

  const priceDifference =
    trialCreditCents && trialCreditCents < price
      ? ((price - trialCreditCents) / 100).toFixed(0)
      : ((trialCreditCents - price) / 100).toFixed(0);

  const priceString =
    trialCreditCents && trialCreditCents < price ? (
      <span>
        You are using&nbsp;
        <strong>
          ${(trialCreditCents / 100).toFixed(0)}
          &nbsp;in credits
        </strong>
        &nbsp;after this instance is created.
        <Hidden smDown>
          <br />
        </Hidden>
        <Hidden mdUp>&nbsp;</Hidden>
        You will be charged&nbsp;
        <strong>${priceDifference}</strong>
        &nbsp;today.
        <Hidden smDown>
          <br />
        </Hidden>
        <Hidden mdUp>&nbsp;</Hidden>
        You will be charged&nbsp;
        <strong>
          ${(price / 100).toFixed(0)}
          &nbsp;monthly
        </strong>
        &nbsp;after applying your credits.
      </span>
    ) : (
      <span>
        After this instance is created you will have a balance of&nbsp;
        <strong>
          ${priceDifference}
          &nbsp;in credits.
        </strong>
        <Hidden smDown>
          <br />
        </Hidden>
        <Hidden mdUp>&nbsp;</Hidden>
        You will be charged&nbsp;
        <strong>$0</strong>
        &nbsp;today.
        <Hidden smDown>
          <br />
        </Hidden>
        <Hidden mdUp>&nbsp;</Hidden>
        After using all of your credits you will be charged&nbsp;
        <strong>
          ${(price / 100).toFixed(0)}
          &nbsp;per month.
        </strong>
      </span>
    );

  const cakeIcon = (
    <div className={classes.cakeIcon}>
      <Cake fontSize="large" color="secondary" />
    </div>
  );

  const mobileCakeIcon = (
    <div className={classes.mobileCakeIcon}>
      <Cake fontSize="large" color="secondary" />
    </div>
  );

  const creditCalculation =
    trialCreditCents > 0 ? (
      <React.Fragment>
        <Hidden mdUp>
          <div className={classes.mobileCreditText}>
            {mobileCakeIcon}
            <ORTypography color="secondary" align="center" className={classes.mobilePriceText}>
              {priceString}
            </ORTypography>
          </div>
        </Hidden>
        <Hidden smDown>
          <div className={classes.creditText}>
            {cakeIcon}
            <ORTypography color="secondary" align="right" className={classes.priceText}>
              {priceString}
            </ORTypography>
          </div>
        </Hidden>
      </React.Fragment>
    ) : (
      <div className={classes.noTrialCreditsText}>
        <ORTypography color="secondary" align="right" className={classes.priceText}>
          You will be charged&nbsp;
          <strong>${(price / 100).toFixed(0)}</strong>
          &nbsp;today.
        </ORTypography>
      </div>
    );

  const nonOwnerContent = (
    <DialogContent classes={{ root: classes.billingDialogContent }}>
      <ORTypography align="center" variant="h5">
        Please contact your organization owner to submit a method of payment
        <br />
        to get unlimited access to all of ObjectRocket&apos;s platform.
      </ORTypography>
    </DialogContent>
  );

  const fineText =
    price > trialCreditCents ? (
      <ORTypography variant="subtitle2" align="center" className={classes.fineText}>
        <i>By confirming this amount, you are agreeing to a recurring monthly payment.</i>
      </ORTypography>
    ) : (
      <ORTypography variant="subtitle2" align="center" className={classes.fineText}>
        {/* eslint-disable-next-line max-len */}
        <i>
          Your credit card will not be charged until you’ve used all your credits, and you can
          delete your instance at any time.
        </i>
        <Hidden smDown>
          <br />
        </Hidden>
        <Hidden mdUp>&nbsp;</Hidden>
        <i>By confirming this amount, you are agreeing to a recurring monthly payment.</i>
      </ORTypography>
    );

  return (
    <Drawer
      classes={{ paper: classes.billingDialog }}
      anchor="bottom"
      open={open}
      onClose={billingOpen}
    >
      <Fab
        onClick={billingOpen}
        size="small"
        classes={{ root: classes.billingCancelButton }}
        aria-label="close"
      >
        <Close fontSize="small" />
      </Fab>
      {!isBillingUser ? (
        nonOwnerContent
      ) : (
        <React.Fragment>
          <DialogTitle classes={{ root: classes.billingDialogTitle }}>
            <ORTypography align="center" variant="h5">
              Please confirm your method of payment to get
              <br />
              unlimited access to all of ObjectRocket&apos;s platform.
            </ORTypography>
          </DialogTitle>
          <DialogContent classes={{ root: classes.billingDialogContent }}>
            <AppBar
              elevation={0}
              position="static"
              classes={{ colorPrimary: classes.billingDialogAppBar }}
            >
              {/*
                Tabs will possibly contain more payment options in the future.
                Remember to add the dynamic value for the tabs
                and an onChange attribute when more options are available
              */}
              <Tabs
                indicatorColor="primary"
                value={0}
                classes={{ root: classes.billingDialogTabs }}
              >
                <Tab label="Credit Card" icon={<CreditCard />} />
              </Tabs>
            </AppBar>
            <BillingFormWrapper
              screen="create"
              onSuccess={billingCompleteHandler}
              allowMultipleSubmissions={false}
              buttonText="Create Instance"
              buttonProps={{
                variant: 'contained',
                size: 'large',
                color: 'primary',
                fullWidth: true,
                className: classes.billingSubmitButton,
              }}
              className={classes.billingDialogForm}
              innerText={creditCalculation}
            />
            {fineText}
          </DialogContent>
        </React.Fragment>
      )}
    </Drawer>
  );
}

const styles = (theme) => ({
  supportLink: {
    color: theme.customColors.white,
  },
  billingDialog: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'unset',
    borderTop: `4px solid ${theme.customColors.teal}`,
  },
  billingDialogTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${theme.spacing(2)}px 0 0 0`,
  },
  billingDialogContent: {
    minWidth: '50%',
    maxWidth: '800px',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      maxWidth: 'unset',
    },
    margin: `${theme.spacing(1)}px 0px`,
    padding: theme.spacing(1),
  },
  billingDialogAppBar: {
    color: theme.customColors.black,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    border: `1px solid ${theme.customColors.teal}`,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  billingDialogTabs: {
    backgroundColor: 'white',
  },
  billingDialogForm: {
    marginBottom: theme.spacing(1),
  },
  billingSubmitButton: {
    margin: '8px 0 8px 0',
  },
  billingCancelButton: {
    position: 'absolute',
    top: theme.spacing(-2.5),
    right: 0,
    zIndex: 900,
    color: theme.customColors.white,
    backgroundColor: theme.customColors.teal,
    '&:hover': {
      background: theme.customColors.teal,
    },
  },
  creditText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
  mobileCreditText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexFlow: 'column nowrap',
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
  noTrialCreditsText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexFlow: 'column nowrap',
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
  cake: {
    color: theme.customColors.teal,
    fontSize: 30,
    paddingTop: 8,
    marginRight: 7,
  },
  cakeIcon: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileCakeIcon: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '8px 0',
  },
  priceText: {
    fontSize: '11px',
    paddingLeft: theme.spacing(3),
  },
  mobilePriceText: {
    fontSize: '11px',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  fineText: {
    fontSize: '10px',
  },
});

UnstyledBillingPage.propTypes = propTypes;
UnstyledBillingPage.defaultProps = defaultProps;

export default withStyles(styles)(UnstyledBillingPage);
